// src/services/ingredientService.ts
import { supabase } from '../utils/supabaseClient';
import {
    Ingredient,
    Group,
    GroupIngredient,
    ItemIngredient,
    CreateIngredientPayload,
    UpdateIngredientPayload,
    CreateGroupPayload,
    UpdateGroupPayload,
    AddIngredientsToGroupPayload,
    AddItemIngredientPayload,
    GroupWithIngredients,
    ItemIngredientWithDetails
} from '../types/ingredient';

export class IngredientService {
    // Ingredient CRUD operations
    static async getIngredientsByBranch(branchId: string): Promise<Ingredient[]> {
        const { data, error } = await supabase
            .from('ingredients')
            .select('*')
            .eq('branch_id', branchId)
            .order('name');

        if (error) {
            throw new Error(`Failed to fetch ingredients: ${error.message}`);
        }

        return data || [];
    }

    static async createIngredient(payload: CreateIngredientPayload): Promise<Ingredient> {
        const { data, error } = await supabase
            .from('ingredients')
            .insert([payload])
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to create ingredient: ${error.message}`);
        }

        return data;
    }

    static async updateIngredient(id: string, payload: UpdateIngredientPayload): Promise<Ingredient> {
        const { data, error } = await supabase
            .from('ingredients')
            .update(payload)
            .eq('id', id)
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to update ingredient: ${error.message}`);
        }

        return data;
    }

    static async deleteIngredient(id: string): Promise<void> {
        const { error } = await supabase
            .from('ingredients')
            .delete()
            .eq('id', id);

        if (error) {
            throw new Error(`Failed to delete ingredient: ${error.message}`);
        }
    }

    // Group CRUD operations
    static async getGroupsByBranch(branchId: string): Promise<GroupWithIngredients[]> {
        const { data, error } = await supabase
            .from('groups')
            .select(`
        *,
        group_ingredients!inner(
          ingredient:ingredients(*)
        )
      `)
            .eq('branch_id', branchId)
            .order('name');

        if (error) {
            throw new Error(`Failed to fetch groups: ${error.message}`);
        }

        // Transform the data to include ingredient count and ingredients array
        return (data || []).map(group => ({
            ...group,
            ingredients: group.group_ingredients?.map((gi: any) => gi.ingredient) || [],
            ingredient_count: group.group_ingredients?.length || 0
        }));
    }

    static async createGroup(payload: CreateGroupPayload): Promise<Group> {
        const { data, error } = await supabase
            .from('groups')
            .insert([payload])
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to create group: ${error.message}`);
        }

        return data;
    }

    static async updateGroup(id: string, payload: UpdateGroupPayload): Promise<Group> {
        const { data, error } = await supabase
            .from('groups')
            .update(payload)
            .eq('id', id)
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to update group: ${error.message}`);
        }

        return data;
    }

    static async deleteGroup(id: string): Promise<void> {
        // First delete all group_ingredients relationships
        await supabase
            .from('group_ingredients')
            .delete()
            .eq('group_id', id);

        // Then delete the group
        const { error } = await supabase
            .from('groups')
            .delete()
            .eq('id', id);

        if (error) {
            throw new Error(`Failed to delete group: ${error.message}`);
        }
    }

    // Group-Ingredient relationship operations
    static async addIngredientsToGroup(payload: AddIngredientsToGroupPayload): Promise<GroupIngredient[]> {
        const insertData = payload.ingredient_ids.map(ingredientId => ({
            group_id: payload.group_id,
            ingredient_id: ingredientId
        }));

        const { data, error } = await supabase
            .from('group_ingredients')
            .insert(insertData)
            .select();

        if (error) {
            throw new Error(`Failed to add ingredients to group: ${error.message}`);
        }

        return data || [];
    }

    static async removeIngredientFromGroup(groupId: string, ingredientId: string): Promise<void> {
        const { error } = await supabase
            .from('group_ingredients')
            .delete()
            .eq('group_id', groupId)
            .eq('ingredient_id', ingredientId);

        if (error) {
            throw new Error(`Failed to remove ingredient from group: ${error.message}`);
        }
    }

    static async getGroupIngredients(groupId: string): Promise<Ingredient[]> {
        const { data, error } = await supabase
            .from('group_ingredients')
            .select(`
        ingredient:ingredients(*)
      `)
            .eq('group_id', groupId);

        if (error) {
            throw new Error(`Failed to fetch group ingredients: ${error.message}`);
        }

        return (data || []).map((item: any) => item.ingredient);
    }

    // Item-Ingredient relationship operations
    static async getItemIngredients(itemId: string): Promise<ItemIngredientWithDetails[]> {
        const { data, error } = await supabase
            .from('item_ingredients')
            .select(`
        *,
        ingredient:ingredients(*),
        group:groups(
          *,
          group_ingredients(
            ingredient:ingredients(*)
          )
        )
      `)
            .eq('item_id', itemId);

        if (error) {
            throw new Error(`Failed to fetch item ingredients: ${error.message}`);
        }

        return data || [];
    }

    static async addItemIngredient(payload: AddItemIngredientPayload): Promise<ItemIngredient> {
        const { data, error } = await supabase
            .from('item_ingredients')
            .insert([payload])
            .select()
            .single();

        if (error) {
            throw new Error(`Failed to add ingredient to item: ${error.message}`);
        }

        return data;
    }

    static async removeItemIngredient(id: string): Promise<void> {
        const { error } = await supabase
            .from('item_ingredients')
            .delete()
            .eq('id', id);

        if (error) {
            throw new Error(`Failed to remove ingredient from item: ${error.message}`);
        }
    }

    static async removeAllItemIngredients(itemId: string): Promise<void> {
        const { error } = await supabase
            .from('item_ingredients')
            .delete()
            .eq('item_id', itemId);

        if (error) {
            throw new Error(`Failed to remove all ingredients from item: ${error.message}`);
        }
    }

    // Utility functions
    static async getAvailableIngredientsForGroup(branchId: string, groupId?: string): Promise<Ingredient[]> {
        let query = supabase
            .from('ingredients')
            .select('*')
            .eq('branch_id', branchId);

        if (groupId) {
            // Get ingredients not already in this group
            const { data: groupIngredients } = await supabase
                .from('group_ingredients')
                .select('ingredient_id')
                .eq('group_id', groupId);

            const usedIngredientIds = groupIngredients?.map(gi => gi.ingredient_id) || [];

            if (usedIngredientIds.length > 0) {
                query = query.not('id', 'in', `(${usedIngredientIds.join(',')})`);
            }
        }

        const { data, error } = await query.order('name');

        if (error) {
            throw new Error(`Failed to fetch available ingredients: ${error.message}`);
        }

        return data || [];
    }

    static async searchIngredients(branchId: string, searchTerm: string): Promise<Ingredient[]> {
        const { data, error } = await supabase
            .rpc('search_ingredients', {
                p_input: {
                    branch_id: branchId,
                    search: searchTerm
                }
            });

        if (error) {
            throw new Error(`Failed to search ingredients: ${error.message}`);
        }

        // The RPC returns an object with data, message, and success properties
        if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
            const response = data as { success: boolean; data: Ingredient[] };
            if (response.success && Array.isArray(response.data)) {
                return response.data;
            }
        }

        return [];
    }

    static async searchGroups(branchId: string, searchTerm: string): Promise<Group[]> {
        const { data, error } = await supabase
            .from('groups')
            .select('*')
            .eq('branch_id', branchId)
            .ilike('name', `%${searchTerm}%`)
            .order('name')
            .limit(20);

        if (error) {
            throw new Error(`Failed to search groups: ${error.message}`);
        }

        return data || [];
    }
}

export const ingredientService = IngredientService;